/**
 * Custom sort dropdown for Collection Product Grid Plus Ultra.
 * Desktop: floating panel under the trigger.
 * Mobile: bottom sheet above the mobile toolbar, with fade/slide animation.
 */
if (!customElements.get('hdt-sort-dropdown-plus-ultra')) {
  customElements.define(
    'hdt-sort-dropdown-plus-ultra',
    class HdtSortDropdownPlusUltra extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.trigger = this.querySelector('[data-hdt-sort-trigger]');
        this.labelEl = this.querySelector('[data-hdt-sort-label]');
        this.panel = this.querySelector('[data-hdt-sort-panel]');
        this.sheet = this.querySelector('[data-hdt-sort-sheet]');
        this.select = this.querySelector('select[name="sort_by"]');
        this.isOpen = false;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onKeydown = this.onKeydown.bind(this);
        this.onResize = this.onResize.bind(this);

        this.trigger?.addEventListener('click', this.onTriggerClick);
        this.addEventListener('click', this.onOptionClick);
        this.sheet?.addEventListener('click', this.onOptionClick);
        this.sheet?.querySelector('[data-hdt-sort-close]')?.addEventListener('click', this.onCloseClick);
        this.sheet?.querySelector('[data-hdt-sort-overlay]')?.addEventListener('click', this.onOverlayClick);
        document.addEventListener('click', this.onDocumentClick);
        document.addEventListener('keydown', this.onKeydown);
        window.addEventListener('resize', this.onResize);

        /* Keep sheet at body level so fixed positioning clears the toolbar. */
        if (this.sheet && this.sheet.parentElement !== document.body) {
          document.body.appendChild(this.sheet);
        }

        this.syncMobileSortField(this.select?.value);
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        document.removeEventListener('keydown', this.onKeydown);
        window.removeEventListener('resize', this.onResize);
        this.sheet?.remove();
      }

      get isMobile() {
        return window.matchMedia('(max-width: 749px)').matches;
      }

      onTriggerClick(event) {
        event.preventDefault();
        event.stopPropagation();
        if (this.isOpen) this.close();
        else this.open();
      }

      onOptionClick(event) {
        const option = event.target.closest('[data-hdt-sort-option]');
        if (!option || (!this.contains(option) && !this.sheet?.contains(option))) return;
        event.preventDefault();
        this.choose(option.dataset.value, option.textContent.trim());
      }

      onCloseClick(event) {
        event.preventDefault();
        this.close();
      }

      onOverlayClick() {
        this.close();
      }

      onDocumentClick(event) {
        if (!this.isOpen || this.isMobile) return;
        if (this.contains(event.target)) return;
        this.close();
      }

      onKeydown(event) {
        if (!this.isOpen) return;
        if (event.key === 'Escape') this.close();
      }

      onResize() {
        if (!this.isOpen) return;
        /* Switch between desktop panel / mobile sheet if breakpoint changes. */
        this.close();
      }

      open() {
        this.isOpen = true;
        this.classList.add('is-open');
        this.trigger?.setAttribute('aria-expanded', 'true');

        if (this.isMobile) {
          this.panel?.setAttribute('hidden', '');
          if (this.sheet) {
            this.sheet.removeAttribute('hidden');
            requestAnimationFrame(() => {
              this.sheet.classList.add('is-open');
            });
          }
          document.body.classList.add('overflow-hidden');
        } else {
          this.sheet?.setAttribute('hidden', '');
          this.sheet?.classList.remove('is-open');
          if (this.panel) {
            this.panel.removeAttribute('hidden');
            requestAnimationFrame(() => {
              this.panel.classList.add('is-open');
            });
          }
        }
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        this.trigger?.setAttribute('aria-expanded', 'false');

        if (this.panel) {
          this.panel.classList.remove('is-open');
          window.setTimeout(() => {
            if (!this.isOpen) this.panel.setAttribute('hidden', '');
          }, 200);
        }

        if (this.sheet) {
          this.sheet.classList.remove('is-open');
          window.setTimeout(() => {
            if (!this.isOpen) this.sheet.setAttribute('hidden', '');
          }, 280);
        }

        document.body.classList.remove('overflow-hidden');
      }

      choose(value, label) {
        if (!value || !this.select) {
          this.close();
          return;
        }

        const changed = this.select.value !== value;
        this.select.value = value;
        this.syncMobileSortField(value);

        if (this.labelEl && label) this.labelEl.textContent = label;

        this.querySelectorAll('[data-hdt-sort-option]').forEach((btn) => {
          const selected = btn.dataset.value === value;
          btn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        });
        this.sheet?.querySelectorAll('[data-hdt-sort-option]').forEach((btn) => {
          const selected = btn.dataset.value === value;
          btn.classList.toggle('is-selected', selected);
          btn.setAttribute('aria-selected', selected ? 'true' : 'false');
        });

        this.close();

        if (changed) {
          this.select.dispatchEvent(new Event('input', { bubbles: true }));
        }
      }

      syncMobileSortField(value) {
        const mobileForm = document.getElementById('FacetFiltersFormMobile');
        if (!mobileForm) return;
        let hidden = mobileForm.querySelector('input[name="sort_by"]');
        if (!hidden) {
          hidden = document.createElement('input');
          hidden.type = 'hidden';
          hidden.name = 'sort_by';
          mobileForm.appendChild(hidden);
        }
        hidden.value = value || this.select?.value || '';
      }
    }
  );
}
